/**
 * MappingTable — the builder's main column: direction toolbar (segmented +
 * confidence/settled stats), issue + AI banners, column headers, field rows,
 * transform-vocabulary footer note.
 */
import React from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { colors, fontFamily, fontSize, fontWeight, radii } from '../../theme';
import { Sparkles, Warning } from '../../icons';
import { useBridge } from '../../store/bridgeStore';
import { useBuilder } from './useBuilder';
import FieldRow from './FieldRow';

const GRID = 'minmax(0,1fr) 116px minmax(0,1fr) 96px 132px';

export default function MappingTable() {
  const { state, actions } = useBridge();
  const b = useBuilder();
  const dir = state.direction;

  return (
    <View style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' } as any}>
      {/* direction toolbar */}
      <View style={styles.toolbar}>
        <View style={styles.seg}>
          <DirSeg label={'SK → ' + b.vendorName} active={dir === 'outbound'} issues={b.outIssues} onPress={() => actions.setDirection('outbound')} />
          <DirSeg label={b.vendorName + ' → SK'} active={dir === 'inbound'} issues={b.inIssues} onPress={() => actions.setDirection('inbound')} />
        </View>
        <Text style={styles.hint}>{b.directionHint}</Text>
        <View style={{ flex: 1 }} />
        <View style={styles.stats}>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={styles.statBig}>{b.meanConf}</Text>
            <Text style={styles.statKey}>confidence</Text>
          </View>
          <View style={{ alignItems: 'flex-end' }}>
            <Text style={[styles.statBig, { color: colors.ink } as any]}>{b.settledLabel}</Text>
            <Text style={styles.statKey}>settled</Text>
          </View>
        </View>
      </View>

      <ScrollView style={{ flex: 1, backgroundColor: colors.paper }} contentContainerStyle={styles.scrollInner}>
        <View style={{ maxWidth: 900, width: '100%', marginHorizontal: 'auto' } as any}>
          {b.entityMappingLoading ? (
            <View style={styles.loadingBanner}>
              <Text style={styles.loadingText}>Loading real mapping from {b.vendorName}…</Text>
            </View>
          ) : b.entityMappingError ? (
            <View style={[styles.banner, { borderColor: colors.warnLine, backgroundColor: colors.warnSoft } as any]}>
              <Warning size={16} color={colors.warn} />
              <Text style={styles.bannerText}>{b.entityMappingError}</Text>
            </View>
          ) : null}
          {!b.entityMappingLoading && b.hasIssues && (
            <View style={[styles.banner, { borderColor: colors.warnLine, backgroundColor: colors.warnSoft } as any]}>
              <Warning size={16} color={colors.warn} />
              <Text style={styles.bannerText}>
                <Text style={{ color: colors.ink, fontWeight: fontWeight.semibold } as any}>{b.issueCountLabel}</Text>
                {' need a decision before this mapping can be approved. Look for the amber rows.'}
              </Text>
            </View>
          )}
          {!b.entityMappingLoading && b.aiCount > 0 && (
            <View style={[styles.banner, { borderColor: colors.infoLine, backgroundColor: colors.infoSoft } as any]}>
              <Sparkles size={16} color={colors.info} />
              <Text style={[styles.bannerText, { color: colors.ink2 } as any]}>{b.aiNoteLabel}</Text>
            </View>
          )}

          {!b.entityMappingLoading && (
            <>
              {/* column headers */}
              {React.createElement(
                'div',
                { style: { display: 'grid', gridTemplateColumns: GRID, gap: 14, padding: '0 16px 8px', alignItems: 'end' } },
                <Text style={[styles.colHead, { color: colors.accent } as any]} key="1">Canonical</Text>,
                <Text style={[styles.colHead, { color: colors.ink3, textAlign: 'center' } as any]} key="2">Transform</Text>,
                <Text style={[styles.colHead, { color: colors.neutral } as any]} key="3">{'Vendor · ' + b.vendorName}</Text>,
                <Text style={[styles.colHead, { color: colors.ink3 } as any]} key="4">Confidence</Text>,
                <Text style={[styles.colHead, { color: colors.ink3, textAlign: 'right' } as any]} key="5">Status</Text>,
              )}

              <View style={{ gap: 8 }}>
                {b.fieldRows.map(f => <FieldRow key={f.fid} f={f} vendorName={b.vendorName} schemaLabel={b.schemaLabel} />)}
              </View>
            </>
          )}

          <View style={styles.footerNote}>
            <Text style={styles.footerNoteText}>
              Transforms come from a fixed vocabulary (type-coerce, enum-remap, format, join, default, passthrough). Border translation only — no triggers, branching, or code.
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
}

function DirSeg({ label, active, issues, onPress }: { label: string; active: boolean; issues: number; onPress: () => void }) {
  return (
    <Pressable onPress={onPress} style={[styles.dirSeg, active ? styles.dirSegActive : null]}>
      <Text style={[styles.dirSegText, active ? { color: colors.ink } as any : null]}>{label}</Text>
      {issues > 0 && <View style={styles.issueBadge}><Text style={styles.issueBadgeText}>{issues}</Text></View>}
    </Pressable>
  );
}

const styles = StyleSheet.create({
  toolbar: { flexShrink: 0, paddingVertical: 12, paddingHorizontal: 24, borderBottomWidth: 1, borderBottomColor: colors.line, flexDirection: 'row', alignItems: 'center', gap: 18, backgroundColor: colors.card },
  seg: { flexDirection: 'row', gap: 4, backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, borderRadius: radii.r3, padding: 3 },
  dirSeg: { borderRadius: radii.r2, paddingVertical: 7, paddingHorizontal: 13, flexDirection: 'row', alignItems: 'center', gap: 7, cursor: 'pointer' } as any,
  dirSegActive: { backgroundColor: colors.card, boxShadow: 'var(--shadow-sm)' } as any,
  dirSegText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, fontWeight: fontWeight.medium, color: colors.ink3 },
  issueBadge: { minWidth: 17, height: 17, paddingHorizontal: 5, borderRadius: radii.full, backgroundColor: colors.warn, alignItems: 'center', justifyContent: 'center' },
  issueBadgeText: { fontFamily: fontFamily.sans, fontSize: 10, fontWeight: fontWeight.semibold, color: '#fff' },
  hint: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3 },
  stats: { flexDirection: 'row', alignItems: 'center', gap: 20 },
  statBig: { fontFamily: fontFamily.sans, fontSize: fontSize.lg, fontWeight: fontWeight.semibold, color: colors.accent, lineHeight: 16 },
  statKey: { fontFamily: fontFamily.sans, fontSize: 10, color: colors.ink3, textTransform: 'uppercase', letterSpacing: 0.4 },
  scrollInner: { paddingVertical: 22, paddingHorizontal: 24 },
  banner: { flexDirection: 'row', alignItems: 'center', gap: 10, paddingVertical: 11, paddingHorizontal: 14, borderWidth: 1, borderRadius: radii.r3, marginBottom: 16 },
  loadingBanner: { alignItems: 'center', paddingVertical: 40 },
  loadingText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink3 },
  bannerText: { fontFamily: fontFamily.sans, fontSize: fontSize.sm, color: colors.ink2, flex: 1, lineHeight: 19 },
  colHead: { fontFamily: fontFamily.sans, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: fontWeight.semibold },
  footerNote: { marginTop: 16, paddingVertical: 11, paddingHorizontal: 14, borderWidth: 1, borderColor: colors.lineStrong, borderStyle: 'dashed', borderRadius: radii.r3 },
  footerNoteText: { fontFamily: fontFamily.sans, fontSize: fontSize.xs, color: colors.ink3, lineHeight: 18 },
});
